// src/components/ui/AppSidebar.jsx
//
// Sidebar de una sola columna con items que se despliegan en dropdowns
// (y sub-dropdowns) en vez del esquema anterior de riel + panel de detalle
// (ModernTwoLevelSidebar). El contenido -- rutas, roles, badges -- es el
// mismo que tenía el sub-sidebar viejo, solo cambia cómo se presenta.
import { normalizarTexto } from '../../utils/textoBusqueda';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import logoH from '../../assets/logo-h.png';
import {
  LayoutDashboard,
  HardDrive,
  Clipboard,
  BadgeCheck,
  BookOpen,
  BarChart2,
  LayoutGrid,
  UtensilsCrossed,
  PlusCircle,
  Network,
  Building,
  LogOut,
  Database,
  Laptop,
  Monitor,
  Package,
  Hash
} from 'lucide-react';
// Iconos animados (hover) -- ver src/components/icons/index.js. No todo
// lucide-react tiene version animada disponible; el resto sigue arriba.
import {
  Users,
  FileText,
  Settings,
  ChevronDown,
  Search,
  ExternalLink,
  User,
  X,
  ShieldCheck,
  DollarSign,
  UserCheck,
  Key,
  Activity,
  FolderOpen,
  Layers
} from '../icons';

import { useAuth } from '../../contexts/AuthContext';
import { usePendingCounts } from '../../hooks/usePendingCounts';
import { useUserSubroles } from '../../hooks/useUserSubroles';
import { usuariosAPI } from '../../services/api';
import { LOGIN_CENTRAL_URL } from '../../config';
import { isotipoApp } from '../../utils/assets';

export default function AppSidebar({ isOpen, toggleSidebar }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pendingCounts = usePendingCounts();
  const { getActiveSubroles } = useUserSubroles();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openIds, setOpenIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [misApps, setMisApps] = useState([]);
  const [catalogoApps, setCatalogoApps] = useState([]);

  const isAdmin = user?.role === 'admin';
  const isSoporte = user?.role === 'soporte';
  const isStaff = isAdmin || isSoporte;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!user) return;
    usuariosAPI.getMisApps()
      .then(res => setMisApps(res.data || []))
      .catch(() => setMisApps([]));
  }, [user]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    usuariosAPI.getPermisosCatalogo()
      .then(res => setCatalogoApps((res.data?.apps || []).filter(a => a.codigo !== 'helpdesk' && a.activa)))
      .catch(() => setCatalogoApps([]));
  }, [user?.role]);

  const activeSubroles = useMemo(() => getActiveSubroles() || [], [getActiveSubroles]);

  const handleOpenExternal = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    if (isMobile && isOpen) toggleSidebar();
  };

  const handleNavClick = () => {
    if (isMobile && isOpen) toggleSidebar();
  };

  const handleLogout = () => {
    if (window.confirm('¿Deseas cerrar tu sesión?')) {
      logout();
      navigate('/login');
    }
  };

  // Misma lógica de "¿esta ruta está activa?" que tenía el sub-sidebar viejo
  // -- aware de query params (pestañas, filtros, categorías).
  const checkIsActive = useCallback((to) => {
    if (!to) return false;
    const [destPath, destQueryString] = to.split('?');
    const currentPath = location.pathname;
    if (currentPath !== destPath) return false;

    const currentParams = new URLSearchParams(location.search);
    const destParams = destQueryString ? new URLSearchParams(destQueryString) : new URLSearchParams();

    if (currentPath === '/configuracion') {
      const currentTab = currentParams.get('tab') || 'users';
      const destTab = destParams.get('tab') || 'users';
      return currentTab === destTab;
    }

    if (currentPath === '/activos') {
      const currentTab = currentParams.get('tab') || 'activos';
      const destTab = destParams.get('tab') || 'activos';
      if (currentTab !== destTab) return false;
      if (currentTab === 'activos') {
        const currentCat = currentParams.get('categoria') || '';
        const destCat = destParams.get('categoria') || '';
        const currentFiltro = currentParams.get('filtro') || '';
        const destFiltro = destParams.get('filtro') || '';
        return currentCat === destCat && currentFiltro === destFiltro;
      }
      return true;
    }

    if (currentPath === '/usuarios') {
      const currentFiltro = currentParams.get('filtro') || 'all';
      const destFiltro = destParams.get('filtro') || 'all';
      return currentFiltro === destFiltro;
    }

    if (destQueryString) {
      for (const [k, v] of destParams.entries()) {
        if (currentParams.get(k) !== v) return false;
      }
      return true;
    }

    return location.search === '' || location.search === '?';
  }, [location.pathname, location.search]);

  // ------------------------------------------------------------------
  // Árbol de navegación: mismo contenido/condiciones que tenía el
  // sub-sidebar (sidebarContentMap) + el riel (navRailItems), ahora como
  // una sola estructura de grupos con items que pueden anidar children.
  // ------------------------------------------------------------------
  const navGroups = useMemo(() => {
    const groups = [];

    // --- Grupo superior: accesos directos sin agrupar ---
    const topItems = [];
    topItems.push({ id: 'almuerzos', label: 'Almuerzos', icon: UtensilsCrossed, to: '/almuerzos', prefixes: ['/almuerzos'] });
    topItems.unshift({ id: 'dashboard', label: isStaff ? 'Dashboard' : 'Inicio', icon: LayoutDashboard, to: '/dashboard' });
    if (topItems.length) groups.push({ items: topItems });

    // --- Grupo "Mi Espacio": lo personal del usuario (sus tickets, actas y
    // activos), separado de lo que gestiona el equipo de TI. Solo para
    // staff/admin; el resto de los usuarios los ve dentro de cada modulo. ---
    if (isStaff) {
      groups.push({
        heading: 'Mi Espacio',
        items: [
          { id: 'mi.nuevo', label: 'Crear Ticket', icon: PlusCircle, to: '/mis-tickets?action=new' },
          { id: 'mi.tickets', label: 'Mis Tickets', icon: Clipboard, badge: pendingCounts.tickets, to: '/mis-tickets', prefixes: ['/mis-tickets'] },
          { id: 'mi.actas', label: 'Mis Actas por Firmar', icon: FileText, badge: pendingCounts.actas, to: '/actas', prefixes: ['/actas'] },
          { id: 'mi.activos', label: 'Mis Activos Asignados', icon: Laptop, to: '/mis-activos', prefixes: ['/mis-activos'] },
        ],
      });
    }

    // --- Grupo "Módulos" ---
    const moduleItems = [];

    if (isStaff) {
      moduleItems.push({
        id: 'nomina',
        label: 'Nómina',
        icon: Users,
        to: '/usuarios?filtro=all',
        prefixes: ['/usuarios', '/mi-equipo'],
        children: [
          { id: 'nomina.todos', label: 'Todos los Colaboradores', icon: Users, to: '/usuarios?filtro=all' },
          { id: 'nomina.nuevo', label: 'Registrar Colaborador', icon: PlusCircle, to: '/usuarios?nuevo=1' },
          ...(user?.esJefatura ? [{ id: 'nomina.miequipo', label: 'Mi Equipo (Jefatura)', icon: Network, to: '/mi-equipo' }] : []),
          {
            id: 'nomina.filtros',
            label: 'Filtros Rápidos',
            icon: Hash,
            children: [
              { id: 'nomina.filtros.activos', label: 'Colaboradores Activos', to: '/usuarios?filtro=activos' },
              { id: 'nomina.filtros.inactivos', label: 'Desvinculados / Inactivos', to: '/usuarios?filtro=inactivos' },
              { id: 'nomina.filtros.conactivos', label: 'Con Activos Asignados', to: '/usuarios?filtro=conActivos' },
              { id: 'nomina.filtros.sinactivos', label: 'Sin Activos Asignados', to: '/usuarios?filtro=sinActivos' }
            ]
          }
        ]
      });
    }

    // Mi Equipo como item propio para quien tiene jefatura pero no es staff
    if (user?.esJefatura && !isStaff) {
      moduleItems.push({ id: 'mi-equipo', label: 'Mi Equipo', icon: Network, to: '/mi-equipo', prefixes: ['/mi-equipo'] });
    }

    moduleItems.push({
      id: 'activos',
      label: 'Activos',
      icon: HardDrive,
      to: isStaff ? '/activos?tab=activos' : '/mis-activos',
      prefixes: isStaff ? ['/activos'] : ['/activos', '/mis-activos'],
      children: [
        ...(!isStaff ? [{ id: 'activos.mios', label: 'Mis Activos Asignados', icon: Laptop, to: '/mis-activos' }] : []),
        ...(isStaff ? [
          { id: 'activos.inventario', label: 'Inventario General (Activos Fijos)', icon: HardDrive, to: '/activos?tab=activos' },
          { id: 'activos.insumos.link', label: 'Gestión de Insumos', icon: Package, to: '/activos?tab=consumibles' },
          { id: 'activos.nuevo', label: 'Registrar Nuevo Activo', icon: PlusCircle, to: '/activos?nuevo=1' },
          {
            id: 'activos.hardware',
            label: 'Hardware TI',
            icon: Monitor,
            children: [
              { id: 'activos.hardware.todos', label: 'Todos los Activos Fijos', to: '/activos?tab=activos' },
              { id: 'activos.hardware.sinasignar', label: 'Sin Asignar (Alerta)', to: '/activos?tab=activos&filtro=sinAsignar' },
              { id: 'activos.hardware.equipos', label: 'Equipos (Laptops / PCs)', to: '/activos?tab=activos&categoria=Equipos' },
              { id: 'activos.hardware.moviles', label: 'Móviles y Celulares', to: '/activos?tab=activos&categoria=Móviles' },
              { id: 'activos.hardware.monitores', label: 'Monitores', to: '/activos?tab=activos&categoria=Monitores' },
              { id: 'activos.hardware.red', label: 'Redes y Comunicaciones', to: '/activos?tab=activos&categoria=Red' }
            ]
          },
          {
            id: 'activos.insumos',
            label: 'Insumos y Consumibles',
            icon: Package,
            children: [
              { id: 'activos.insumos.todos', label: 'Todos los Insumos', to: '/activos?tab=consumibles' },
              { id: 'activos.insumos.accesorios', label: 'Accesorios', to: '/activos?tab=consumibles&categoria=Accesorios' },
              { id: 'activos.insumos.papeleria', label: 'Papelería', to: '/activos?tab=consumibles&categoria=Papelería' },
              { id: 'activos.insumos.toner', label: 'Toner', to: '/activos?tab=consumibles&categoria=Toner' },
              { id: 'activos.insumos.cables', label: 'Cables', to: '/activos?tab=consumibles&categoria=Cables' },
              { id: 'activos.insumos.perifericos', label: 'Periféricos', to: '/activos?tab=consumibles&categoria=Periféricos' },
              { id: 'activos.insumos.otros', label: 'Otros', to: '/activos?tab=consumibles&categoria=Otros' }
            ]
          }
        ] : [])
      ]
    });

    moduleItems.push({
      id: 'actas',
      label: 'Actas',
      icon: FileText,
      badge: pendingCounts.actas,
      to: isStaff ? '/gestion-actas' : '/actas',
      prefixes: isStaff ? ['/gestion-actas'] : ['/actas', '/gestion-actas'],
      children: [
        ...(!isStaff ? [{ id: 'actas.mias', label: 'Mis Actas por Firmar', icon: FileText, badge: pendingCounts.actas, to: '/actas' }] : []),
        ...(isStaff ? [
          { id: 'actas.gestion', label: 'Gestión General de Actas', icon: FolderOpen, to: '/gestion-actas' },
          { id: 'actas.generar', label: 'Generar Nueva Acta', icon: PlusCircle, to: '/gestion-actas?action=generar' }
        ] : [])
      ]
    });

    moduleItems.push({
      id: 'tickets',
      label: 'Tickets',
      icon: Clipboard,
      badge: pendingCounts.tickets,
      to: isStaff ? '/tickets' : '/mis-tickets',
      prefixes: isStaff ? ['/tickets', '/admin/categorias-ticket'] : ['/tickets', '/mis-tickets', '/admin/categorias-ticket'],
      children: [
        ...(!isStaff ? [{ id: 'tickets.nuevo', label: 'Crear Nuevo Ticket', icon: PlusCircle, to: '/mis-tickets?action=new' }] : []),
        ...(!isStaff ? [{ id: 'tickets.mios', label: 'Mis Tickets', icon: Clipboard, badge: pendingCounts.tickets, to: '/mis-tickets' }] : []),
        ...(isStaff ? [
          { id: 'tickets.bandeja', label: 'Bandeja General (Todos)', icon: FolderOpen, to: '/tickets' },
          { id: 'tickets.asignados', label: 'Mis Tickets Asignados', icon: Clipboard, to: '/tickets?tab=assigned' },
          { id: 'tickets.nuevo.usuario', label: 'Crear Ticket a Usuario', icon: PlusCircle, to: '/tickets?action=new-user' },
          { id: 'tickets.categorias', label: 'Categorías de Tickets', icon: Settings, to: '/admin/categorias-ticket' }
        ] : [])
      ]
    });

    // Paz y Salvo: solo si administra el proceso o tiene subrol de firmante
    if (isStaff || user?.role === 'rrhh' || activeSubroles.length > 0) {
      const pazItems = [];
      if (isAdmin) {
        pazItems.push(
          { id: 'paz.admin', label: 'Gestión Completa (Admin)', icon: BadgeCheck, badge: pendingCounts.pazysalvo, to: '/pazysalvo-admin' },
          { id: 'paz.rrhh', label: 'RRHH / Gestión', icon: Users, to: '/pazysalvo' },
          { id: 'paz.jefe', label: 'Jefatura Directa', icon: UserCheck, to: '/pazysalvo/jefe-directo' },
          { id: 'paz.ti', label: 'TI / Informática', icon: ShieldCheck, to: '/pazysalvo/ti' },
          { id: 'paz.contabilidad', label: 'Contabilidad', icon: DollarSign, to: '/pazysalvo/contabilidad' },
          { id: 'paz.finanzas', label: 'Gerencia Finanzas', icon: Building, to: '/pazysalvo/gerencia-finanzas' },
          { id: 'paz.gerente', label: 'Gerente General', icon: BadgeCheck, to: '/pazysalvo/gerente-general' }
        );
      } else if (activeSubroles.length > 0) {
        activeSubroles.forEach((sub, idx) => {
          let route = '/pazysalvo';
          let label = `Paz y Salvo - ${sub.rol}`;
          if (sub.rol === 'JefeInmediato') { route = '/pazysalvo/jefe-directo'; label = 'Paz y Salvo - Jefatura'; }
          else if (sub.rol === 'RRHH') { route = '/pazysalvo'; label = 'Paz y Salvo - Gestión RRHH'; }
          else if (sub.rol === 'TI' || sub.rol === 'Informatica') { route = '/pazysalvo/ti'; label = 'Paz y Salvo - TI'; }
          else if (sub.rol === 'Contabilidad') { route = '/pazysalvo/contabilidad'; label = 'Paz y Salvo - Contabilidad'; }
          else if (sub.rol === 'GerenciaFinanzas') { route = '/pazysalvo/gerencia-finanzas'; label = 'Paz y Salvo - Finanzas'; }
          else if (sub.rol === 'GerenteGeneral') { route = '/pazysalvo/gerente-general'; label = 'Paz y Salvo - Gerencia Gral.'; }
          pazItems.push({ id: `paz.sub${idx}`, label, icon: BadgeCheck, badge: pendingCounts.pazysalvo, to: route });
        });
      } else {
        pazItems.push({ id: 'paz.mios', label: 'Mis Procesos', icon: BadgeCheck, to: '/pazysalvo' });
      }
      moduleItems.push({
        id: 'pazysalvo',
        label: 'Paz y Salvo',
        icon: BadgeCheck,
        badge: pendingCounts.pazysalvo,
        to: (isAdmin || user?.role === 'rrhh') ? '/pazysalvo-admin' : '/pazysalvo',
        prefixes: ['/pazysalvo'],
        children: pazItems
      });
    }

    moduleItems.push({
      id: 'conocimiento',
      label: 'Base de Conocimiento',
      icon: BookOpen,
      to: '/base-conocimiento',
      prefixes: ['/base-conocimiento'],
      children: [
        { id: 'conocimiento.articulos', label: 'Artículos y Guías', icon: BookOpen, to: '/base-conocimiento' },
        ...(isStaff ? [{ id: 'conocimiento.admin', label: 'Administrar Artículos', icon: Settings, to: '/base-conocimiento/admin' }] : [])
      ]
    });

    // Tickets va primero en el listado de módulos (todos los roles)
    {
      const idx = moduleItems.findIndex((m) => m.id === 'tickets');
      if (idx > 0) moduleItems.unshift(moduleItems.splice(idx, 1)[0]);
    }

    groups.push({ heading: 'Módulos', items: moduleItems });

    // --- Grupo "Reportes" (staff) ---
    if (isStaff) {
      groups.push({ items: [{ id: 'reportes', label: 'Reportes', icon: BarChart2, to: '/reportes', prefixes: ['/reportes'] }] });
    }

    // --- Aplicaciones del holding (solo admin): usuarios, accesos y permisos de cada app ---
    if (isAdmin) {
      groups.push({ items: [{ id: 'aplicaciones', label: 'Aplicaciones', icon: LayoutGrid, to: '/aplicaciones', prefixes: ['/aplicaciones'] }] });
    }

    // --- Grupo "Accesos Directos" (portales externos) ---
    const externalApps = isAdmin ? catalogoApps : misApps;
    if (externalApps.length > 0) {
      groups.push({
        heading: 'Accesos Directos',
        items: externalApps.map(app => ({
          id: `acceso.${app.codigo}`,
          label: app.nombre,
          icon: ExternalLink,
          isExternal: true,
          url: `${LOGIN_CENTRAL_URL}/?app=${app.codigo}`
        }))
      });
    }

    return groups;
  }, [user, isStaff, isAdmin, activeSubroles, pendingCounts, misApps, catalogoApps]);

  // Item de Configuración, fijo abajo (fuera del scroll, como antes)
  const configItem = useMemo(() => ({
    id: 'configuracion',
    label: 'Configuración',
    icon: Settings,
    to: isAdmin ? '/configuracion?tab=users' : '/perfil',
    prefixes: ['/configuracion', '/perfil', '/ajustes', '/evidencias-seguridad'],
    children: [
      { id: 'config.perfil', label: 'Perfil de Usuario', icon: User, to: '/perfil' },
      { id: 'config.firma', label: 'Firma Digital y MFA', icon: Key, to: '/perfil' },
      ...(isAdmin ? [
        { id: 'config.sso', label: 'Usuarios Autenticados (SSO)', icon: UserCheck, to: '/configuracion?tab=users' },
        { id: 'config.jerarquia', label: 'Jerarquía Organizacional', icon: Network, to: '/configuracion?tab=hierarchy' },
        { id: 'config.rolespaz', label: 'Roles de Paz y Salvo', icon: BadgeCheck, to: '/configuracion?tab=pazysalvo-roles' },
        { id: 'config.sistema', label: 'Configuración del Sistema', icon: Settings, to: '/configuracion?tab=system' },
        { id: 'config.logs', label: 'Logs y Auditoría', icon: Activity, to: '/configuracion?tab=logs' },
        { id: 'config.o365', label: 'Microsoft O365', icon: Database, to: '/configuracion?tab=microsoft-o365' },
        { id: 'config.evidencias', label: 'Evidencias de Seguridad', icon: ShieldCheck, to: '/evidencias-seguridad' }
      ] : [])
    ]
  }), [isAdmin]);

  // Auto-expandir el grupo (y sub-grupo) donde está la ruta activa, para que
  // al entrar por URL directa el item activo quede visible.
  useEffect(() => {
    const allTop = navGroups.flatMap(g => g.items).concat([configItem]);
    setOpenIds(prev => {
      const next = new Set(prev);
      const walk = (items) => {
        items.forEach(it => {
          if (it.prefixes?.some(p => location.pathname.startsWith(p))) {
            next.add(it.id);
          }
          if (it.children) walk(it.children);
        });
      };
      walk(allTop);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleOpen = (id) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Filtro de búsqueda: conserva un item si su label matchea, o si algún
  // descendiente matchea (en cuyo caso también se auto-expande).
  const term = searchTerm.trim().toLowerCase();
  const filterTree = useCallback((items) => {
    if (!term) return items;
    return items.reduce((acc, it) => {
      const selfMatch = normalizarTexto(it.label).includes(normalizarTexto(term));
      const filteredChildren = it.children ? filterTree(it.children) : undefined;
      if (selfMatch || (filteredChildren && filteredChildren.length > 0)) {
        acc.push({ ...it, children: filteredChildren });
      }
      return acc;
    }, []);
  }, [term]);

  useEffect(() => {
    if (!term) return;
    // expandir todo lo que matchea mientras se busca
    const allTop = navGroups.flatMap(g => g.items).concat([configItem]);
    setOpenIds(prev => {
      const next = new Set(prev);
      const walk = (items) => {
        items.forEach(it => {
          if (it.children) {
            next.add(it.id);
            walk(it.children);
          }
        });
      };
      walk(allTop);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const visibleGroups = navGroups
    .map(g => ({ ...g, items: filterTree(g.items) }))
    .filter(g => g.items.length > 0);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-xs z-40 md:hidden transition-opacity"
          onClick={toggleSidebar}
        />
      )}

      <aside
        className={`fixed md:relative inset-y-0 left-0 z-50 flex flex-col h-full max-h-screen w-72 select-none
          bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 shadow-2xl md:shadow-none
          transition-transform duration-300 shrink-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        {/* Encabezado / marca */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <button
            onClick={() => { navigate('/dashboard'); if (isMobile && isOpen) toggleSidebar(); }}
            className="h-9 w-9 flex items-center justify-center rounded-lg shadow-sm hover:opacity-80 transition-opacity shrink-0"
            title="HelpDesk"
          >
            <img src={logoH} alt="HelpDesk" className="h-full w-full object-contain" />
          </button>
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-sm text-gray-900 dark:text-white leading-tight truncate">HelpDesk</span>
            <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">Portal Corporativo</span>
          </div>
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="ml-auto p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
              title="Cerrar menú"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Buscador */}
        <div className="px-3 pt-3 shrink-0">
          <div className="relative">
            <div className="absolute left-2.5 inset-y-0 flex items-center pointer-events-none">
              <Search className="h-3.5 w-3.5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar en menú..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Lista de navegación */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700">
          {visibleGroups.map((group, gIdx) => (
            <div key={gIdx} className="flex flex-col gap-0.5">
              {group.heading && (
                <span className="px-2.5 mb-1 text-[10px] font-bold tracking-wider text-gray-400 dark:text-gray-500 uppercase">
                  {group.heading}
                </span>
              )}
              {group.items.map(item => (
                <NavTreeItem
                  key={item.id}
                  item={item}
                  level={0}
                  openIds={openIds}
                  toggleOpen={toggleOpen}
                  checkIsActive={checkIsActive}
                  onNavigate={handleNavClick}
                  onOpenExternal={handleOpenExternal}
                  currentPath={location.pathname}
                />
              ))}
            </div>
          ))}
          {visibleGroups.length === 0 && (
            <p className="px-2.5 text-xs text-gray-400 text-center py-4">Sin resultados para "{searchTerm}"</p>
          )}
        </nav>

        {/* Configuración + perfil + logout, fijos abajo */}
        <div className="shrink-0 border-t border-gray-100 dark:border-gray-800 px-2.5 py-2 flex flex-col gap-0.5">
          {(misApps.length > 0 || catalogoApps.length > 0) && (
            <div className="flex items-center gap-2.5 px-2.5 py-1.5 text-[10px] font-bold tracking-wider text-gray-400 dark:text-gray-500 uppercase">
              <Layers size={12} /> Sistema
            </div>
          )}
          <NavTreeItem
            item={configItem}
            level={0}
            openIds={openIds}
            toggleOpen={toggleOpen}
            checkIsActive={checkIsActive}
            onNavigate={handleNavClick}
            onOpenExternal={handleOpenExternal}
            currentPath={location.pathname}
          />

          <div className="flex items-center justify-between rounded-lg px-2 py-1.5 mt-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <button
              onClick={() => { navigate('/perfil'); if (isMobile && isOpen) toggleSidebar(); }}
              className="flex items-center gap-2.5 min-w-0 text-left"
              title={`Perfil: ${user?.nombre || 'Usuario'}`}
            >
              <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 flex items-center justify-center font-bold text-xs text-blue-600 dark:text-blue-400 shrink-0">
                {user?.nombre?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-medium text-gray-900 dark:text-white truncate">{user?.nombre || 'Usuario'}</span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate">{user?.role || 'Colaborador'}</span>
              </div>
            </button>
            <button
              onClick={handleLogout}
              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ml-1 shrink-0"
              title="Cerrar Sesión"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

// Nodo recursivo: link directo, item externo, o dropdown con children
// (que a su vez pueden tener sus propios children -- sub-dropdowns).
function NavTreeItem({ item, level, openIds, toggleOpen, checkIsActive, onNavigate, onOpenExternal, currentPath }) {
  const Icon = item.icon || Hash;
  const hasChildren = !!item.children?.length;
  const isOpenNode = openIds.has(item.id);
  const isActive = item.to ? checkIsActive(item.to) : false;
  // Un grupo se marca "activo" también si la ruta actual cae dentro de sus prefijos,
  // aunque el propio grupo no tenga `to` exacto seleccionado.
  const isGroupActive = !isActive && item.prefixes?.some(p => currentPath.startsWith(p));

  if (item.isExternal) {
    return (
      <button
        onClick={() => onOpenExternal(item.url)}
        className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors group"
        style={{ paddingLeft: `${level * 14 + 10}px` }}
      >
        <img src={isotipoApp(item.id)} alt="" className="w-[18px] h-[18px] object-contain shrink-0" />
        <span className="truncate flex-1 text-left">{item.label}</span>
        <ExternalLink size={12} className="text-gray-300 group-hover:text-blue-600 transition-colors shrink-0" />
      </button>
    );
  }

  if (hasChildren) {
    return (
      <div className="flex flex-col w-full">
        <button
          onClick={() => {
            toggleOpen(item.id);
            // Si el item también tiene ruta propia, navegar al abrir (como antes)
          }}
          className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs font-medium transition-colors
            ${isActive || isGroupActive
              ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          style={{ paddingLeft: `${level * 14 + 10}px` }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Icon size={level === 0 ? 16 : 15} className={`shrink-0 ${isActive || isGroupActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
            <span className="truncate">{item.label}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {item.badge > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full leading-none">
                {item.badge}
              </span>
            )}
            <ChevronDown
              size={13}
              className={`text-gray-400 transition-transform duration-200 ${isOpenNode ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        <div
          className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out ${
            isOpenNode ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden min-h-0 relative flex flex-col gap-0.5 mt-0.5">
            <div
              className="absolute top-0 bottom-0 border-l border-gray-200 dark:border-gray-700"
              style={{ left: `${level * 14 + 17.5}px` }}
            />
            {item.children.map(child => (
              <NavTreeItem
                key={child.id}
                item={child}
                level={level + 1}
                openIds={openIds}
                toggleOpen={toggleOpen}
                checkIsActive={checkIsActive}
                onNavigate={onNavigate}
                onOpenExternal={onOpenExternal}
                currentPath={currentPath}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={`flex items-center justify-between px-2.5 py-2 text-xs font-medium rounded-lg transition-colors
        ${isActive
          ? 'bg-blue-50 text-blue-700 font-semibold dark:bg-blue-900/30 dark:text-blue-300'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
      style={{ paddingLeft: `${level * 14 + 10}px` }}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Icon size={level === 0 ? 16 : 15} className={`shrink-0 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
        <span className="truncate">{item.label}</span>
      </div>
      {item.badge > 0 && (
        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full leading-none shrink-0">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}
